import { nanoid } from 'nanoid/non-secure';
import Quill, { type EmitterSource, type Op, type Range } from 'quill';
import Delta from 'quill-delta';
import QuillImageDropAndPaste, { type ImageData } from 'quill-image-drop-and-paste';
import { Mention as QuillMention } from 'quill-mention';
import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  concatAll,
  concatMap,
  debounceTime,
  delay,
  distinct,
  filter,
  Observable,
  Subject,
  Subscription,
  tap,
} from 'rxjs';
import { z } from 'zod';
import QuillMentionBlot from '@/components/conversations/conversation-form/quill-editor/mention-blot';
import MentionMenuItem, { type MentionMenuItemProps } from '@/components/conversations/mention-menu/mention-menu';
import { getUserPlatform } from '@/hooks/use-user-platform';
import { uploadableScheme } from '@/models/uploadable';
import type CommManager from '@/services/comm-manager';
import { BUILD_ENV } from '@/utils/constants';
import { FileType } from '@/utils/file-type';
import { createLogger } from '@/utils/logger';
import { convertFileToUploadable } from '@/utils/uploadable';

const endWithDoubleWhitespaceRegExp = /\s\s+$/g;

export type MentionSource = {
  id: number;
  value: string;
  image?: string;
  color?: string;
  name?: string;
  remarkName?: string;
  role?: number;
};

export enum QuillModule {
  Mention = 'mention',
  DropAndPaste = 'imageDropAndPaste',
}

interface MentionModule {
  options: {
    source: (searchTerm: string, renderList: (mentionSource: MentionSource[], searchTerm: string) => void) => void;
  };
  mentionContainer?: HTMLElement;
  renderList: (mentionSource?: MentionSource[], searchTerm?: string) => void;
  lastSearchTerm?: string;
}

export function createQuillManager(commManager?: CommManager | undefined) {
  return new QuillManager(commManager);
}

Quill.register({
  'blots/mention': QuillMentionBlot,
  'modules/mention': QuillMention,
  'modules/imageDropAndPaste': QuillImageDropAndPaste,
});
// Quill.debug(IS_DEV);

export default class QuillManager {
  readonly #logger = createLogger('Quill');
  readonly #commManager: CommManager | undefined;
  #quill: Quill | null = null;
  #moduleEnabled: QuillModule[] = [];
  #ghostInput: HTMLInputElement | null = null;
  #container: HTMLElement | null = null;
  #mentions: MentionSource[] | null = null;
  #lastSelection: Range | null = null;
  #isInitialized = false;
  #isShowingMentionMenu = false;
  #isComposingText = false;
  #enterAsSubmit = false;
  #event$ = new Subject<QuillEvent>();
  #subscriptions$$ = new Subscription();

  constructor(commManager: CommManager | undefined) {
    this.#commManager = commManager;

    this.focus = this.focus.bind(this);
    this.blur = this.blur.bind(this);
    this.reset = this.reset.bind(this);
    this.insertText = this.insertText.bind(this);
    this.insertMention = this.insertMention.bind(this);
    this.insertFile = this.insertFile.bind(this);
    this.dismissMentionMenu = this.dismissMentionMenu.bind(this);
    this.#handleKeyPress = this.#handleKeyPress.bind(this);
    this.#handleComposingText = this.#handleComposingText.bind(this);
    this.#handleBlur = this.#handleBlur.bind(this);
    this.#handlePaste = this.#handlePaste.bind(this);
  }

  initialize(container: HTMLElement, placeholder = '', installModules: QuillModule[] = []) {
    if (this.#isInitialized) return;

    this.#isInitialized = true;
    this.#moduleEnabled = installModules;
    this.#container = container;

    const modules: Record<string, unknown> = {
      imageDropAndPaste: {
        handler: (_: unknown, type: string, imageData: ImageData) => this.#handlePaste(type, imageData),
      },
    };

    if (installModules.includes(QuillModule.Mention)) {
      modules.mention = {
        mentionDenotationChars: ['@'],
        // allow all language characters, special characters, numbers and empty string
        allowedChars: /^\S*$/u,
        renderItem: (data: MentionMenuItemProps, searchTerm: string) => {
          const container = document.createElement('div');
          const root = ReactDOM.createRoot(container);
          root.render(React.createElement(MentionMenuItem, { ...data, searchTerm }));
          return container;
        },
        isolateCharacter: true,
        source: (searchTerm: string, renderList: (mentionSource: MentionSource[], searchTerm: string) => void) => {
          renderList(this.#searchMentionSources(searchTerm), searchTerm);
        },
      };
    }

    const quill = new Quill(container, {
      placeholder,
      formats: ['mention'],
      modules,
    });

    quill.container.querySelector('.ql-editor')?.classList.add('trigger-window-resize');

    quill.on('text-change', () => {
      const contents = quill.getContents();
      if (contents) {
        this.#event$.next({
          action: 'content',
          data: parseContent(contents.ops),
        });
      }
    });
    quill.on('selection-change', (range, _, source) => {
      if (range === null) {
        this.#handleBlur();
        this.#event$.next({
          action: 'focus',
          data: false,
        });
      } else if (source === Quill.sources.USER) {
        this.#lastSelection = range;
        this.#event$.next({
          action: 'focus',
          data: true,
        });
      }
    });

    quill.container.addEventListener('blur', this.#handleBlur);
    quill.container.addEventListener('focusout', this.#handleBlur);
    quill.container.addEventListener('keydown', this.#handleKeyPress);
    quill.container.addEventListener('compositionstart', this.#handleComposingText);
    quill.container.addEventListener('compositionend', this.#handleComposingText);

    if (getUserPlatform().isIOS && BUILD_ENV === 'embedded') {
      this.#logger.info('use ghost input workaround');
      quill.root.addEventListener('pointerdown', this.#handlePointerDown);
      this.#ghostInput = this.#createGhostInput();
    }

    this.#quill = quill;
    setTimeout(() => {
      this.#subscribeMutationObservers();
    }, 600);
  }

  dispose() {
    if (!this.#isInitialized) return;

    this.#isInitialized = false;
    this.#quill?.root?.removeEventListener('pointerdown', this.#handlePointerDown);
    this.#quill?.container?.removeEventListener('blur', this.#handleBlur);
    this.#quill?.container?.removeEventListener('focusout', this.#handleBlur);
    this.#quill?.container?.removeEventListener('keydown', this.#handleKeyPress);
    this.#quill?.container?.removeEventListener('compositionstart', this.#handleComposingText);
    this.#quill?.container?.removeEventListener('compositionend', this.#handleComposingText);

    this.#quill?.off('text-change');
    this.#quill?.off('selection-change');

    this.#commManager?.dispatch({ event: 'dodge-keyboard', data: { toggle: false } });
    document.querySelector('input#ghost-input')?.remove();

    this.#quill = null;
    this.#container = null;
    this.#mentions = null;
    this.#event$ = new Subject<QuillEvent>();
    this.#subscriptions$$.unsubscribe();
    this.#subscriptions$$ = new Subscription();
  }

  get isInitialized() {
    return this.#isInitialized;
  }

  get event$() {
    return this.#event$.pipe(
      filter((event) => !!event?.action),
      distinct(),
    );
  }

  set enterAsSubmit(value: boolean) {
    this.#enterAsSubmit = value;
    this.#container?.querySelector('.ql-editor')?.setAttribute('enterKeyHint', value ? 'send' : 'enter');
  }

  set initialContent(value: string) {
    this.#quill?.setContents(parseAsDelta(value), Quill.sources.SILENT);
  }

  set mentionSources(value: MentionSource[]) {
    this.#mentions = value;

    const mentionModule = this.#quill?.getModule('mention') as MentionModule | undefined;
    if (!mentionModule) return;

    mentionModule.options.source = (searchTerm, renderList) => {
      renderList(this.#searchMentionSources(searchTerm), searchTerm);
    };
  }

  readonly focus = (position?: number, source: EmitterSource = Quill.sources.SILENT) => {
    if (!this.#quill) {
      this.#logger.fatal('QUILL_NOT_INITIALIZED');
      throw 'QUILL_NOT_INITIALIZED';
    }

    const currentIndex = position ?? this.#quill.getSelection()?.index ?? this.#quill.getLength() - 1;
    const range = this.#quill.getSelection()?.length ?? 0;

    this.#quill.setSelection(currentIndex, range, source);

    this.#commManager?.dispatch({
      event: 'dodge-keyboard',
      data: { toggle: true },
    });

    this.#quill.focus();
  };

  readonly blur = () => {
    this.#quill?.blur();
    this.#handleBlur();
  };

  readonly getSelection = () => {
    return this.#quill?.getSelection() || null;
  };

  readonly reset = (keepFocus = true) => {
    if (!this.#quill) return;

    this.#quill.setText('', Quill.sources.SILENT);
    if (keepFocus) this.focus();
    this.#quill.history.clear();
  };

  readonly insertText = (text: string, position?: number, focus = true) => {
    if (!this.#quill) {
      this.#logger.fatal('QUILL_NOT_INITIALIZED');
      throw 'QUILL_NOT_INITIALIZED';
    }

    const currentLength = this.#quill.getLength();
    let currentIndex = position ?? this.#lastSelection?.index ?? this.#quill.getSelection()?.index ?? 0;
    if (currentLength >= 1 && currentIndex >= currentLength) {
      currentIndex = currentLength - 1;
    }
    this.#quill.insertText(currentIndex, text, Quill.sources.USER);
    if (this.#lastSelection) this.#lastSelection.index += text.length;
    if (focus) {
      this.focus(currentIndex + text.length);
    } else {
      this.blur();
    }
  };

  readonly insertMention = (mentionId: number, focus = true) => {
    if (!this.#quill) {
      this.#logger.fatal('QUILL_NOT_INITIALIZED');
      throw 'QUILL_NOT_INITIALIZED';
    }

    const mention = this.#mentions?.find((m) => m.id === mentionId);
    if (!mention) return;

    let currentIndex = this.#quill.getSelection()?.index || this.#quill.getLength() - 1;
    if (currentIndex <= 0) {
      this.#quill.insertText(0, ' ', Quill.sources.SILENT);
      currentIndex = 1;
    }
    this.#quill.insertEmbed(
      currentIndex++,
      'mention',
      {
        ...mention,
        denotationChar: '@',
      },
      Quill.sources.USER,
    );
    this.#quill.insertText(currentIndex++, ' ', Quill.sources.SILENT);
    if (focus) this.focus(currentIndex);
  };

  readonly insertFile = (file: File) => {
    const uploadable = convertFileToUploadable(file);
    let type: FileType;
    switch (uploadable.type.split('/')[0]) {
      case 'image':
        type = FileType.Image;
        break;
      case 'video':
        type = FileType.Video;
        break;
      default:
        type = FileType.File;
        break;
    }
    this.#event$.next({
      action: 'attachment',
      data: {
        type,
        uploadable,
      },
    });
  };

  readonly dismissMentionMenu = () => {
    if (!this.#moduleEnabled.includes(QuillModule.Mention)) return;

    const mentionModule = this.#quill?.getModule('mention') as MentionModule | undefined;

    if (!mentionModule) {
      this.#logger.warn('MENTION_MODULE_NOT_FOUND');
    } else {
      this.#isShowingMentionMenu = false;
      mentionModule.renderList([]);
    }
  };

  #subscribeMutationObservers() {
    const mentionMenuContainer = document.getElementById('mention-menu-container');

    if (!mentionMenuContainer || !this.#container) {
      if (this.#moduleEnabled.includes(QuillModule.Mention)) this.#logger.warn('CONTAINER_NOT_FOUND');
      return;
    }

    const targetObserver$$ = observeOnMutation(this.#container, { childList: true, subtree: false })
      .pipe(
        debounceTime(200),
        concatAll(),
        filter((mutation) => mutation.addedNodes.length > 0),
        tap(() => {
          this.#isShowingMentionMenu = true;
        }),
        concatMap((mutation) => mutation.addedNodes),
        filter(
          (addedNode) => addedNode instanceof HTMLElement && addedNode.classList.contains('ql-mention-list-container'),
        ),
      )
      .subscribe({
        next: (node) => {
          mentionMenuContainer?.append(node);
        },
      });
    const mentionMenuContainerObserver$$ = observeOnMutation(mentionMenuContainer, { childList: true, subtree: false })
      .pipe(
        debounceTime(200),
        concatAll(),
        filter((mutation) => mutation.addedNodes.length === 0),
        delay(100),
      )
      .subscribe({
        next: () => {
          this.#isShowingMentionMenu = false;
        },
      });

    this.#subscriptions$$.add(targetObserver$$);
    this.#subscriptions$$.add(mentionMenuContainerObserver$$);
  }

  #searchMentionSources(searchTerm: string): MentionSource[] {
    if (!this.#mentions) return [];
    if (!searchTerm) return this.#mentions;

    const normalizedSearchTerm = new RegExp(searchTerm.toLowerCase(), 'gi');
    return this.#mentions.filter(({ name, value }) =>
      name ? name.match(normalizedSearchTerm) : value.match(normalizedSearchTerm),
    );
  }

  readonly #handlePaste = (type: string, imageData: ImageData) => {
    const blob = imageData.toBlob();
    if (blob.type.startsWith('image')) {
      const file = new File([blob], `${nanoid(16)}.png`, { type: blob.type });
      this.insertFile(file);
    } else {
      this.#logger.warn('NOT_SUPPORTED_PASTE_TYPE', type);
    }
  };

  readonly #handleKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey && !this.#isShowingMentionMenu && !this.#isComposingText) {
      event.preventDefault();
      event.stopPropagation();
      if (this.#enterAsSubmit) {
        const lastOp = this.#quill?.getContents().ops.at(-1);
        if (typeof lastOp?.insert === 'string' && lastOp.insert.match(endWithDoubleWhitespaceRegExp)) {
          this.#quill?.setContents(new Delta().delete(1), Quill.sources.SILENT);
          this.#event$.next({ action: 'submit' });
        }
      }
    }
  };

  readonly #handleComposingText = (event: CompositionEvent) => {
    this.#isComposingText = event.type !== 'compositionend';
  };

  readonly #handleBlur = () => {
    this.#commManager?.dispatch({ event: 'dodge-keyboard', data: { toggle: false } });
    this.#event$.next({ action: 'draft' });
    this.dismissMentionMenu();
  };

  readonly #handlePointerDown = (event: PointerEvent) => {
    event.stopImmediatePropagation();
    event.stopPropagation();
    event.preventDefault();
    if (this.#quill?.hasFocus()) return;
    this.#ghostInput?.focus();
  };

  #createGhostInput() {
    let input = document.querySelector<HTMLInputElement>('input#ghost-input');

    if (input) {
      input?.remove();
    }

    input = document.createElement('input');
    input.type = 'text';
    input.id = 'ghost-input';
    input.style.position = 'absolute';
    input.style.top = '-100%';
    input.onfocus = () => {
      setTimeout(() => {
        this.focus(undefined, Quill.sources.USER);
      }, 300);
    };

    document.body.appendChild(input);

    return input;
  }
}

function parseAsDelta(htmlString: Nullable<string>): Op[] {
  if (!htmlString) return [];

  const processedHtmlString = `${htmlString}\u0020`;
  const regex = /<span data-at-id=['"](.+?)['"]>(.+?)<\/span>/g;
  const parts = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(processedHtmlString)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ insert: processedHtmlString.slice(lastIndex, match.index) });
    }

    if (match[1])
      parts.push({
        insert: {
          mention: { id: +match[1], value: `${match[2]}`, denotationChar: '', image: '' },
        },
      });
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < processedHtmlString.length) {
    parts.push({ insert: processedHtmlString.slice(lastIndex) });
  }

  return parts;
}

function parseContent(contents: Nullable<Op[]>): string {
  if (!contents) return '';

  return contents.reduce((acc, cur) => {
    let value = acc;

    if (typeof cur.insert === 'string') {
      value += cur.insert;
    }

    if (typeof cur.insert === 'object' && cur.insert.mention) {
      const mention = cur.insert.mention as MentionMenuItemProps;
      value += `<span data-at-id="${mention.id}">${mention.denotationChar}${mention.value}</span>`;
    }

    return value;
  }, '');
}

function observeOnMutation(target: HTMLElement, config?: MutationObserverInit): Observable<MutationRecord[]> {
  return new Observable((observer) => {
    const mutationObserver = new MutationObserver((mutations) => {
      observer.next(mutations);
    });

    mutationObserver.observe(target, config);

    return () => {
      mutationObserver.disconnect();
    };
  });
}

const quillEventSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('attachment'),
    data: z.object({
      type: z.nativeEnum(FileType),
      uploadable: uploadableScheme,
    }),
  }),
  z.object({
    action: z.literal('content'),
    data: z.string().trim().nullable(),
  }),
  z.object({
    action: z.literal('draft'),
  }),
  z.object({
    action: z.literal('submit'),
  }),
  z.object({
    action: z.literal('focus'),
    data: z.boolean(),
  }),
]);

type QuillEvent = z.infer<typeof quillEventSchema>;
